import { BUILTINS } from './builtins';
import { FormulaRegistry, paramAt } from './registry';
import { inferType } from './typecheck';
import { AstNode, ValueType } from './types';
import { setSpan, spanOf } from './spans';

/**
 * Field refs in first-appearance order, deduplicated. Members of list items
 * ("transactions.distributions") are refs like any other.
 */
export function getReferencedFields(ast: AstNode): string[] {
  const seen = new Set<string>();
  const walk = (node: AstNode): void => {
    switch (node.kind) {
      case 'field':
        seen.add(node.ref);
        return;
      case 'unary':
        walk(node.operand);
        return;
      case 'binary':
        walk(node.left);
        walk(node.right);
        return;
      case 'call':
        node.args.forEach(walk);
        return;
      case 'lambda':
        walk(node.body);
        return;
      case 'let':
        node.bindings.forEach((binding) => walk(binding.value));
        walk(node.body);
        return;
      case 'num':
      case 'str':
      case 'bool':
      case 'param':
        return;
    }
  };
  walk(ast);
  return [...seen];
}

/** Reports a referenced field's declared type; unknown refs default to 'number'. */
export type FieldTypeLookup = (ref: string) => ValueType | undefined;

/**
 * Static result type of a formula the editor has already validated: what the
 * same inference typecheck runs would return, minus the rules — a field the
 * lookup does not know, or a stored formula that predates the rules, still
 * yields a type. Drives the form's Type validation and the format picker.
 */
export function inferResultType(ast: AstNode, getFieldType?: FieldTypeLookup): ValueType {
  return inferType(ast, (ref) => {
    const type = getFieldType?.(ref);
    return type ? { kind: 'scalar', type } : undefined;
  });
}

/**
 * Return a new tree with every field ref mapped through `fn` — e.g. display
 * name → canonical key at save time (throw from `fn` to reject a ref), or the
 * reverse when loading a stored formula for editing.
 *
 * `lists` names the lists whose items are in scope at the ref, innermost
 * first: inside REDUCE(…, [L], LAMBDA(…)) it is ["L"] — with L already mapped
 * through `fn`, since the list argument is transformed before the body — so a
 * display name can resolve to a member of the current item. INDEX's third
 * argument and SUM's second get their own list the same way (INDEX's index
 * expression does not).
 * KPI's three refs carry their slot in `kpiSlot`, so a caller can resolve a
 * KPI name apart from row fields and canonicalize the period/scenario tokens.
 * `fn` is called in source order, so callers can count calls to locate a ref.
 */
export type KpiArgSlot = 'name' | 'period' | 'scenario';

type TransformRef = (ref: string, lists: string[], kpiSlot?: KpiArgSlot) => string;

export function transformFields(
  ast: AstNode,
  fn: TransformRef,
  registry: FormulaRegistry = BUILTINS
): AstNode {
  return transform(ast, fn, [], registry);
}

function transform(ast: AstNode, fn: TransformRef, lists: string[], registry: FormulaRegistry): AstNode {
  const out = transformNode(ast, fn, lists, registry);
  // The output stands where the input was read: a check on the transformed tree can still point there.
  const span = spanOf(ast);
  return span && out !== ast ? setSpan(out, span) : out;
}

function transformNode(ast: AstNode, fn: TransformRef, lists: string[], registry: FormulaRegistry): AstNode {
  const inner = (node: AstNode, scope = lists) => transform(node, fn, scope, registry);
  switch (ast.kind) {
    case 'num':
    case 'str':
    case 'bool':
    case 'param':
      return ast;
    case 'field':
      return { kind: 'field', ref: fn(ast.ref, lists) };
    case 'unary':
      return { kind: 'unary', op: ast.op, operand: inner(ast.operand) };
    case 'binary':
      return { kind: 'binary', op: ast.op, left: inner(ast.left), right: inner(ast.right) };
    case 'lambda':
      return { kind: 'lambda', params: ast.params, body: inner(ast.body) };
    case 'let':
      return {
        kind: 'let',
        bindings: ast.bindings.map(({ name, value }) => ({ name, value: inner(value) })),
        body: inner(ast.body),
      };
    case 'call': {
      const spec = registry.get(ast.fn);
      // A list param puts its list in scope for the member and lambda params after it; kpi
      // and token params say which KPI slot they are.
      let listScope = lists;
      const args = ast.args.map((arg, i): AstNode => {
        const param = spec ? paramAt(spec, i) : undefined;
        switch (param?.kind) {
          case 'list': {
            const out = inner(arg);
            listScope = out.kind === 'field' ? [out.ref, ...lists] : lists;
            return out;
          }
          case 'member':
          case 'lambda':
            return inner(arg, listScope);
          case 'kpi':
          case 'token': {
            const slot: KpiArgSlot = param.kind === 'kpi' ? 'name' : (param.name as KpiArgSlot);
            return arg.kind === 'field' ? { kind: 'field', ref: fn(arg.ref, lists, slot) } : inner(arg);
          }
          default:
            return inner(arg);
        }
      });
      return { kind: 'call', fn: ast.fn, args };
    }
  }
}
